import mixpanel from "mixpanel-browser";
import { ShieldDeviceCheckResponse } from "../components/shieldDeviceCheck";
import { getParentURL } from "../utils/getParentURL";
import { safeLocalStorage } from "./utils";
import { sdkSelect } from "../sdk/utils/sdkVersionSelect";
import { getMHTrackingId } from "./getMHTrackingId";

const deviceInfo = {
  colorDepth: screen.colorDepth,
  screenHeight: screen.height,
  screenWidth: screen.width,
  timeZoneOffset: new Date().getTimezoneOffset(),
};
const deviceInfoHeader = JSON.stringify(deviceInfo);

const urlSearchParams = new URLSearchParams(window.location.search);
const isEmbedBySDK = !!urlSearchParams.get("sdk");

const deviceTrackingId = getMHTrackingId();
export class Config {
  /**
   * API version to use for the requests.
   * v1 | v1.4
   */
  static apiVersion = isEmbedBySDK
    ? sdkSelect({
        0: "v1",
        1: "v1",
        default: "v1.4",
      })
    : "v1.4";
  static api = `${import.meta.env.VITE_API_BASE}/api/${
    Config.apiVersion
  }/client/`;
  static apiBase = `${import.meta.env.VITE_API_BASE}`;
  static requestId = "";
  /**
   * Intent secret passed from JS SDK, check `useSDKApiHandler.ts`
   */
  static intentSecret = "";
  static forwardedQueryParams = ["supports_nfc", "customer"];

  static getApiHeaders(path: string, extraHeaders?: Record<string, string>) {
    const urlParams = new URLSearchParams(window.location.search);
    const isUsingSDK =
      urlParams.get("sdk") === "true" && !!urlParams.get("version");
    const intentSecret =
      Config.intentSecret || urlParams.get("mh_intent_secret");

    if (path.startsWith(import.meta.env.VITE_API_BASE)) {
      return {
        "content-type": "application/json",
        ...(isUsingSDK ? { "x-sdk-version": urlParams.get("version") } : {}),
        ...(intentSecret ? { "x-mh-intent-secret": `${intentSecret}` } : {}),
        "x-tracking-id": mixpanel.get_distinct_id(),
        "x-device-id": deviceTrackingId,
        "x-mh-version": import.meta.env.VITE_MH_VERSION,
        "x-parent-origin": getParentURL(),
        "x-device-info": deviceInfoHeader,
        ...(Config.requestId ? { "x-request-id": Config.requestId } : {}),
        ...extraHeaders,
      };
    }

    return {
      "content-type": "application/json",
    };
  }

  static generatePath({
    fullUrl,
    endpoint,
  }: {
    fullUrl?: boolean;
    endpoint: string;
  }) {
    const urlParams = new URLSearchParams(window.location.search);

    const path = `${fullUrl ? "" : Config.api}${endpoint}`;
    const url = new URL(path);

    Config.forwardedQueryParams.forEach((param) => {
      if (urlParams.has(param)) {
        url.searchParams.append(param, urlParams.get(param));
      }
    });

    return url.toString();
  }
}

// will throw error of type `CustomError` or return success promise
const handleResponse = <T,>(response: Response): Promise<T> => {
  Config.requestId = response.headers.get("x-request-id");
  // we can move the `if (response.json)` here to have default 400 error
  if (!response.ok) {
    // create error instance with HTTP status text
    const error: CustomError = new Error(response.statusText);
    error.isHttpError = true;
    error.fetchStatus = response.status;
    error.url = response.url;
    error.json = response.json();
    throw error;
  }
  return response.json();
};

const getErrorMsgAndCode = async (
  error: CustomError
): Promise<{
  code: number;
  msg: string;
  field: string | null;
  errors: Array<unknown>;
}> => {
  let msg = "";
  let field = null;
  const json = await error.json;

  if (!json || !json?.status?.code) {
    // const url = error.url;
    const fetchStatus = error.fetchStatus;

    // Log not formatted BE error response to FIX on BE
    // if (fetchStatus !== 404) {
    //   logToSentry(
    //     {
    //       url,
    //       return: { code: fetchStatus, msg, json, error },
    //     },
    //     "event",
    //     "no:code"
    //   );
    // }

    return {
      code: fetchStatus,
      msg,
      field,
      errors: [],
    };
  }

  const code = json.status.code;
  // set the JSON response message
  json.status.errors.forEach((_error: Record<string, string>) => {
    Object.keys(_error).map((key) => {
      msg = _error[key];
      field = key;
    });
  });

  return {
    code,
    msg,
    field,
    errors: json.status.errors,
  };
};

const getErrorObjectAndCode = async (
  error: CustomError
): Promise<{ code: number; obj: Record<string, string> }> => {
  const obj: Record<string, string> = {};
  const json = await error.json;
  if (json && json?.status?.errors) {
    const code = json.status.code;
    // set the JSON response message
    json.status.errors.forEach((_error: Record<string, string>) => {
      Object.keys(_error).forEach((key) => {
        obj[key] = _error[key];
      });
    });
    return {
      code,
      obj,
    };
  }

  return {
    code: 500,
    obj: {},
  };
};

const httpGet = <T,>(
  endpoint: string,
  fullUrl?: boolean,
  signal?: AbortSignal,
  headers?: Record<string, string>
): Promise<T> => {
  const path = Config.generatePath({
    fullUrl,
    endpoint,
  });

  return fetchWithRetry<T>(path, {
    headers: Config.getApiHeaders(path, headers),
    signal,
  }).catch((error) => {
    console.error(error);
    throw error;
  });
};

const httpPost = <T,>(
  endpoint: string,
  data?: unknown,
  fullUrl?: boolean,
  headers?: Record<string, string>
): Promise<T> => {
  const path = Config.generatePath({
    fullUrl,
    endpoint,
  });

  return fetchWithRetry<T>(path, {
    method: "post",
    body: data ? JSON.stringify(data) : null,
    headers: Config.getApiHeaders(path, headers),
  }).catch((error) => {
    console.error(error);
    throw error;
  });
};

const httpPut = <T,>(endpoint: string, data?: unknown): Promise<T> => {
  const path = Config.generatePath({
    endpoint,
  });

  return fetchWithRetry<T>(path, {
    method: "put",
    body: data ? JSON.stringify(data) : null,
    headers: Config.getApiHeaders(path),
  }).catch((error) => {
    console.error(error);
    throw error;
  });
};

const httpDelete = <T, U>(endpoint: string, data?: U): Promise<T> => {
  const path = Config.generatePath({
    endpoint,
  });

  return fetchWithRetry<T>(path, {
    method: "delete",
    body: data ? JSON.stringify(data) : null,
    headers: Config.getApiHeaders(path),
  }).catch((error) => {
    console.error(error);
    throw error;
  });
};

const updatePaymentIntent = (
  intentId: string,
  methodName: string,
  payload: Record<string, string | number>
): Promise<PaymentApiResponse> => {
  const url = `payments/intents/${intentId}/${methodName}/`;
  return httpPost<PaymentApiResponse>(url, payload);
};

const processAfterTransition = (
  intentId: string,
  payload?: Record<string, unknown>
): Promise<PaymentApiResponse> => {
  const url = `payments/intents/${intentId}/process-after-transition/`;
  return httpPost<PaymentApiResponse>(
    url,
    payload
      ? payload
      : {
          process_after_transition: true,
        }
  );
};

const fraudDataCollection = ({
  intentId,
  providerId,
  lang,
  fraudProvider,
}: {
  intentId: string;
  lang: string;
  providerId: string;
  fraudProvider: {
    providerId: string;
    data:
      | {
          forterTokenCookie: string;
          userAgent: string;
        }
      | ShieldDeviceCheckResponse
      | {
          is_succeeded: boolean;
          intent_id: string;
        }
      | {
          radar_session: string | undefined;
          radar_err: unknown | undefined;
          moneyhash_secret: string;
        };
  };
}): Promise<PaymentApiResponse> => {
  const url = `payments/intents/${intentId}/data-collection/?lang=${lang}`;
  return httpPost<PaymentApiResponse>(url, {
    provider_id: providerId,
    fraud_data: {
      [fraudProvider.providerId]: fraudProvider.data,
    },
  });
};

const getPaymentIntent = (
  intentId: string,
  lang: string,
  signal?: AbortSignal
): Promise<PaymentApiResponse> => {
  const url = `payments/intents/${intentId}/?lang=${lang}`;
  return httpGet<PaymentApiResponse>(url, false, signal);
};

const backToMerchantAndClosePaymentIntent = (intentId: string) => {
  const url = `intents/${intentId}/back/`;
  return httpPost<PaymentApiResponse | PayoutApiResponse>(url);
};

const updatePaymentMethod = (
  intentId: string,
  lang: string,
  paymentMethodName?: PaymentMethodSlugs,
  usePartialWallet?: boolean,
  loyaltyData?: LoyaltyPayload
): Promise<PaymentApiResponse> => {
  const url = `payments/intents/${intentId}/update-method/?lang=${lang}`;
  let options = {};
  if (paymentMethodName) {
    options = {
      payment_method: paymentMethodName,
      ...(usePartialWallet ? { partial_wallet: true } : {}),
      ...(loyaltyData ? { loyalty: loyaltyData } : {}),
    };
  } else {
    options = { reset_intent: true };
  }
  return httpPost<PaymentApiResponse>(url, options);
};

const retryWithSamePaymentMethod = (
  intentId: string,
  lang: string
): Promise<PaymentApiResponse> => {
  const url = `payments/intents/${intentId}/retry/?lang=${lang}`;

  return httpPost<PaymentApiResponse>(url);
};

const updatePaymentAmount = ({
  intentId,
  templateId,
  amount,
  note,
  lang,
}: {
  intentId: string;
  templateId: string;
  amount: string;
  note: string;
  lang: string;
}): Promise<PaymentApiResponse> => {
  return httpPost(`payments/intents/${intentId}/update/?lang=${lang}`, {
    amount,
    note,
    id: templateId,
  });
};

const getPaymentLinksData = (
  id: string,
  lang: string
): Promise<PaymentLinksApiResponse> => {
  const url = `payment_links/${id}/?lang=${lang}`;
  return httpGet<PaymentLinksApiResponse>(url);
};

const updatePaymentLinkProductQuantity = (
  id: string,
  product: { item: string; quantity: number }
) => {
  return httpPost(
    `${import.meta.env.VITE_API_BASE}/api/${
      Config.apiVersion
    }/payment_links/${id}/update-quantities/`,
    [product],
    true
  );
};

const updateThreeDSecure = (
  isTokenizeCard: boolean,
  intentId: string,
  status: boolean,
  lang: string
): Promise<PaymentApiResponse> => {
  const url = `${
    isTokenizeCard ? "tokens" : "payments"
  }/3d-secure/${intentId}/?lang=${lang}`;
  return httpPost<PaymentApiResponse>(url, {
    three_d_secure_authenticated: status,
  });
};

const submitForm = ({
  tokenizeCard,
  tokenizePayout,
  forPayout,
  intentID,
  paymentMethod,
  data,
  lang,
}: {
  tokenizeCard?: boolean;
  tokenizePayout?: boolean;
  forPayout?: boolean;
  intentID: string;
  paymentMethod?: string;
  data: Record<string, unknown>;
  lang: string;
}):
  | Promise<PaymentApiResponse>
  | Promise<PayoutApiResponse>
  | Promise<CardApiResponse> => {
  if (tokenizePayout) {
    return httpPost<CardApiResponse>(
      `tokens/intents/payout/${intentID}/`,
      data
    );
  }

  const url = `${
    tokenizeCard ? "tokens" : forPayout ? "payout" : "payments"
  }/intents/${intentID}/${
    tokenizeCard ? "" : `${paymentMethod}/`
  }?lang=${lang}`;
  if (tokenizeCard) {
    return httpPost<CardApiResponse>(url, data);
  }
  if (forPayout) {
    return httpPost<PayoutApiResponse>(url, data);
  }
  return httpPost<PaymentApiResponse>(url, data);
};

const deleteCardToken = (
  cardId: string,
  intentSecret: string,
  lang: string
): Promise<void> => {
  const url = `card_tokens/${cardId}/?lang=${lang}`;
  return httpDelete<void, { secret: string }>(url, { secret: intentSecret });
};

const payWithCard = ({
  intentId,
  cardToken,
  cvv,
  lang,
  useWalletBalance,
  installmentPlanData,
  loyaltyData,
}: {
  intentId: string;
  cardToken: string;
  cvv?: string;
  lang: string;
  useWalletBalance?: boolean;
  installmentPlanData?: {
    planId: string;
    issuerCode?: string;
  };
  loyaltyData?: LoyaltyPayload;
}): Promise<PaymentApiResponse> => {
  //TODO: why this ?
  const url = `payments/intents/${intentId}/card_token/?lang=${lang}`;
  safeLocalStorage.removeItem("methodOfOpenedIframe");
  return httpPost<PaymentApiResponse>(url, {
    card_token_id: cardToken,
    ...(cvv ? { cvv } : {}),
    ...(useWalletBalance ? { partial_wallet: true } : {}),
    ...(loyaltyData ? { loyalty: loyaltyData } : {}),
    ...(installmentPlanData?.planId
      ? { installment_id: installmentPlanData.planId }
      : {}),
    ...(installmentPlanData?.issuerCode
      ? { issuer_code: installmentPlanData.issuerCode }
      : {}),
  });
};

const createBankAccountToken = (
  id: string,
  lang: string
): Promise<BankAccountApiResponse> => {
  const url = `tokens/intents/bankaccount/${id}/?lang=${lang}`;
  return httpGet(url);
};

const createPayoutToken = (
  id: string,
  lang: string
): Promise<PayoutTokenApiResponse> => {
  const url = `tokens/intents/payout/${id}/?lang=${lang}`;
  return httpGet(url);
};

const payWithBankAccount = ({
  intentId,
  bankToken,
  lang,
  loyaltyData,
}: {
  intentId: string;
  bankToken: string;
  lang: string;
  loyaltyData?: LoyaltyPayload;
}): Promise<PaymentApiResponse> => {
  const url = `payments/intents/${intentId}/bankaccount_token/?lang=${lang}`;
  return httpPost<PaymentApiResponse>(url, {
    bankaccount_token_id: bankToken,
    ...(loyaltyData ? { loyalty: loyaltyData } : {}),
  });
};

const submitCardCvv = ({
  intentId,
  cvv,
  lang,
  installmentPlanData,
  loyaltyData,
}: {
  intentId: string;
  cvv: string;
  lang: string;
  installmentPlanData?: {
    planId: string;
    issuerCode?: string;
  };
  loyaltyData?: LoyaltyPayload;
}): Promise<PaymentApiResponse> => {
  const url = `payments/intents/${intentId}/cvv/?lang=${lang}`;
  return httpPost<PaymentApiResponse>(url, {
    cvv,
    ...(loyaltyData ? { loyalty: loyaltyData } : {}),
    ...(installmentPlanData?.planId
      ? { installment_id: installmentPlanData.planId }
      : {}),
    ...(installmentPlanData?.issuerCode
      ? { issuer_code: installmentPlanData.issuerCode }
      : {}),
  });
};

const payWithWallet = (
  intentId: string,
  lang: string,
  loyaltyData?: LoyaltyPayload
): Promise<PaymentApiResponse> => {
  const url = `payments/intents/${intentId}/SELFSERVE_WALLET/?lang=${lang}`;
  return httpPost<PaymentApiResponse>(url, {
    use_wallet: true,
    ...(loyaltyData ? { loyalty: loyaltyData } : {}),
  });
};

const payWithPaytabs = (
  baseUrl: string,
  payload: PayTabsPayload
): Promise<{ token: string }> => {
  // cSpell:ignore tokenise
  const url = `${baseUrl}/payment/tokenise`;
  return httpPost(url, payload, true);
};

const payWithDibsy = (
  url: string,
  payload: PayWithDibsy
): Promise<PaymentApiResponse> => {
  return httpPost(url, payload, true);
};

const tokenizeWithPayTab = (
  secret: string,
  token: string,
  lang: string
): Promise<PaymentApiResponse> => {
  const url = `${Config.apiBase}/providers/paytabs/token/${secret}/?lang=${lang}`;
  return httpPost(url, { token }, true);
};

const getInvoice = (id: string, lang: string): Promise<InvoiceApiResponse> => {
  const url = `invoices/${id}/?lang=${lang}`;
  return httpGet(url);
};

const createCardToken = (
  id: string,
  lang: string
): Promise<CardApiResponse> => {
  const url = `tokens/intents/${id}/?lang=${lang}`;
  return httpGet(url);
};

const updatePayoutIntentAmount = (
  intentId: string,
  newAmount: string,
  intentAmount: number,
  lang: string
): Promise<PayoutApiResponse> => {
  const url = `payout/intents/${intentId}/add-amount/?lang=${lang}`;
  const body: { amount: string; reset_amount?: boolean } = {
    amount: newAmount,
  };
  if (Number(intentAmount)) {
    body.reset_amount = true;
  }
  return httpPost<PayoutApiResponse>(url, body);
};

const getPayoutMethod = (
  intentId: string,
  lang: string,
  methodName?: string,
  signal?: AbortSignal
): Promise<PayoutApiResponse> => {
  const url = `payout/intents/${intentId}/${
    methodName ? methodName : ""
  }?lang=${lang}`;
  safeLocalStorage.removeItem("methodOfOpenedIframe");
  return httpGet<PayoutApiResponse>(url, false, signal);
};

const updatePayoutMethod = (
  intentId: string,
  lang: string,
  methodName?: string
): Promise<PayoutApiResponse> => {
  const url = `payout/intents/${intentId}/update-method/?lang=${lang}`;
  let options = {};
  if (methodName) {
    options = { payout_method: methodName };
  } else {
    options = { reset_intent: true };
  }

  return httpPost<PayoutApiResponse>(url, options);
};

const fetchBillingData = (
  id: string,
  lang: string
): Promise<BillingApiResponse> => {
  const url = `${import.meta.env.VITE_API_BASE}/api/${
    Config.apiVersion
  }/billing/${id}/?lang=${lang}`;
  return httpGet(url, true);
};

const submitBillingForm = (
  data: {
    company_name: string;
    company_legal_address: string;
    registration_number: string;
    countries: string[];
    full_name: string;
    email: string;
    phone_number: string;
    role: string;
    user_agreement: boolean;
    terms_and_conditions: boolean;
  },
  id: string,
  lang: string
): Promise<BillingApiResponse> => {
  const url = `${import.meta.env.VITE_API_BASE}/api/${
    Config.apiVersion
  }/billing/${id}/?lang=${lang}`;
  return fetch(url, {
    method: "post",
    body: JSON.stringify(data),
    headers: Config.getApiHeaders(url),
  }).then((res) => {
    if (!res.ok) {
      throw res.json();
    }
    return res.json();
  });
};

const fetchWithRetry = async <T,>(
  url: string,
  options: RequestInit = {},
  retries = 3,
  delay = 500
) => {
  for (let i = 0; i < retries; i++) {
    try {
      const res = await fetch(url, options).then((response) =>
        handleResponse<T>(response)
      );

      return res;
    } catch (err: unknown) {
      const isLastAttempt = i === retries - 1;
      const json = await (err as CustomError).json;
      const isLoadFailed = !json || !json?.status?.code;
      if (isLastAttempt || !isLoadFailed) {
        throw err;
      }
      await new Promise((res) => setTimeout(res, delay));
    }
  }
};

export {
  httpGet,
  httpPut,
  httpPost,
  httpDelete,
  getErrorMsgAndCode,
  getErrorObjectAndCode,
  getPaymentIntent,
  updatePaymentIntent,
  backToMerchantAndClosePaymentIntent,
  updatePaymentMethod,
  retryWithSamePaymentMethod,
  updatePaymentAmount,
  updateThreeDSecure,
  submitForm,
  deleteCardToken,
  payWithCard,
  payWithWallet,
  payWithPaytabs,
  payWithDibsy,
  payWithBankAccount,
  createBankAccountToken,
  createPayoutToken,
  tokenizeWithPayTab,
  getInvoice,
  createCardToken,
  updatePayoutIntentAmount,
  getPayoutMethod,
  updatePayoutMethod,
  fetchBillingData,
  submitBillingForm,
  getPaymentLinksData,
  updatePaymentLinkProductQuantity,
  submitCardCvv,
  processAfterTransition,
  fraudDataCollection,
};
